import { useState, useEffect, useCallback } from "react";
import { UserCog, Plus, KeyRound, Loader2, Power } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { fmtDate, initials } from "@/lib/format";
import { PageHeader, Loading, EmptyState } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";

export default function Staff() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [pwUser, setPwUser] = useState(null);
  const [pw, setPw] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try { const { data } = await api.get("/users"); setItems(data); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const toggleActive = async (u) => {
    try { await api.patch(`/users/${u.id}`, { active: !u.active }); toast.success(u.active ? "Deactivated" : "Activated"); load(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const resetPw = async () => {
    if (pw.length < 6) { toast.error("Min 6 characters"); return; }
    try { await api.post(`/users/${pwUser.id}/reset-password`, { password: pw }); toast.success("Password reset"); setPwUser(null); setPw(""); }
    catch (e) { toast.error(apiError(e)); }
  };

  const roleBadge = { admin: "bg-bk-blue", receptionist: "bg-bk-info", physiotherapist: "bg-bk-green" };

  return (
    <div>
      <PageHeader title="Staff Management" subtitle={`${items.length} users`} testid="staff-page">
        <Button onClick={() => setOpen(true)} data-testid="add-staff-btn" className="bg-bk-green hover:bg-bk-greenDark"><Plus className="h-4 w-4 mr-1" /> Add Staff</Button>
      </PageHeader>

      {loading ? <Loading /> : items.length === 0 ? <EmptyState icon={UserCog} title="No staff" /> : (
        <div className="grid gap-2.5 md:grid-cols-2 lg:grid-cols-3">
          {items.map((u) => (
            <Card key={u.id} data-testid={`staff-${u.email}`} className="p-4">
              <div className="flex items-start gap-3">
                <Avatar className="h-11 w-11"><AvatarFallback className="bg-bk-lightGreen text-bk-green font-semibold">{initials(u.name)}</AvatarFallback></Avatar>
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-slate-800 truncate">{u.name}</p>
                  <p className="text-xs text-slate-500 truncate">{u.email}</p>
                  <div className="flex items-center gap-2 mt-1">
                    <Badge className={`text-[10px] capitalize ${roleBadge[u.role]}`}>{u.role}</Badge>
                    <Badge variant="outline" className={`text-[10px] ${u.active ? "text-bk-green border-green-200" : "text-slate-400"}`}>{u.active ? "Active" : "Inactive"}</Badge>
                  </div>
                </div>
              </div>
              <div className="flex items-center justify-between mt-3 pt-3 border-t">
                <div className="flex items-center gap-2">
                  <Switch checked={u.active} onCheckedChange={() => toggleActive(u)} data-testid={`toggle-active-${u.email}`} />
                  <span className="text-xs text-slate-500">{u.active ? "Active" : "Inactive"}</span>
                </div>
                <Button variant="ghost" size="sm" className="h-8 text-bk-blue" onClick={() => { setPwUser(u); setPw(""); }} data-testid={`reset-pw-${u.email}`}><KeyRound className="h-4 w-4 mr-1" /> Reset</Button>
              </div>
            </Card>
          ))}
        </div>
      )}

      <CreateStaff open={open} onOpenChange={setOpen} onDone={load} />

      <Dialog open={!!pwUser} onOpenChange={(o) => !o && setPwUser(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle className="font-display">Reset Password · {pwUser?.name}</DialogTitle></DialogHeader>
          <div className="space-y-1.5"><Label>New Password</Label><Input type="password" value={pw} onChange={(e) => setPw(e.target.value)} data-testid="new-password" /></div>
          <DialogFooter><Button variant="outline" onClick={() => setPwUser(null)}>Cancel</Button><Button onClick={resetPw} className="bg-bk-green hover:bg-bk-greenDark">Reset</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function CreateStaff({ open, onOpenChange, onDone }) {
  const [f, setF] = useState({ name: "", email: "", phone: "", password: "", role: "receptionist" });
  const [loading, setLoading] = useState(false);
  useEffect(() => { if (open) setF({ name: "", email: "", phone: "", password: "", role: "receptionist" }); }, [open]);
  const set = (k, v) => setF((x) => ({ ...x, [k]: v }));
  const submit = async () => {
    if (!f.name || !f.email || f.password.length < 6) { toast.error("Name, email and 6+ char password required"); return; }
    setLoading(true);
    try { await api.post("/users", f); toast.success("Staff created"); onOpenChange(false); onDone(); }
    catch (e) { toast.error(apiError(e)); } finally { setLoading(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader><DialogTitle className="font-display">Add Staff</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1.5"><Label>Name</Label><Input value={f.name} onChange={(e) => set("name", e.target.value)} data-testid="staff-name" /></div>
          <div className="space-y-1.5"><Label>Email</Label><Input type="email" value={f.email} onChange={(e) => set("email", e.target.value)} data-testid="staff-email" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5"><Label>Phone</Label><Input value={f.phone} onChange={(e) => set("phone", e.target.value)} /></div>
            <div className="space-y-1.5"><Label>Role</Label>
              <Select value={f.role} onValueChange={(v) => set("role", v)}><SelectTrigger data-testid="staff-role"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="receptionist">Receptionist</SelectItem><SelectItem value="physiotherapist">Home Physiotherapist</SelectItem><SelectItem value="admin">Admin</SelectItem></SelectContent></Select>
            </div>
          </div>
          <div className="space-y-1.5"><Label>Password</Label><Input type="password" value={f.password} onChange={(e) => set("password", e.target.value)} data-testid="staff-password" /></div>
        </div>
        <DialogFooter><Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button><Button onClick={submit} disabled={loading} data-testid="staff-save" className="bg-bk-green hover:bg-bk-greenDark">{loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Create"}</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
